// useSmartPoll — تحديث تلقائي احترافي بلا تذبذب.
// المبدأ: لا تُعيد بناء الجدول عند كل نبضة. الخلفية تجلب بصمت، وتطبّق فقط
// إذا تغيّرت البيانات فعلاً (getSignature) وإذا لم يكن المستخدم متفاعلاً
// (isBusy: تمرير/بحث/صف مُحدَّد). أثناء التفاعل تُخزَّن البيانات الجديدة
// كـ"معلّقة" مع عدّادها، فيظهر شريط "N جديد — اضغط للتحديث" بدل القفز.
import { useCallback, useEffect, useRef, useState } from "react";

export function useSmartPoll({
  intervalMs = 30000,
  fetcher,          // async () => data
  getSignature,     // (data) => string  — كشف التغيّر
  getNewCount,      // (next, prev) => number  — لعدّاد الشريط
  isBusy,           // () => bool  — هل المستخدم متفاعل الآن؟
  onError,          // (err) => void  — أخطاء التحميل الأمامي فقط
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);   // للتحميل الأمامي فقط
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pending, setPending] = useState(null);     // بيانات مخزَّنة أثناء التفاعل
  const [pendingCount, setPendingCount] = useState(0);

  const dataRef = useRef(null);
  const sigRef = useRef(null);

  const apply = useCallback((d) => {
    dataRef.current = d;
    sigRef.current = getSignature(d);
    setData(d);
    setLastUpdated(Date.now());
    setPending(null);
    setPendingCount(0);
  }, [getSignature]);

  // تحميل أمامي (أولي/فلتر/يدوي): يُظهر loading ويطبّق دائمًا.
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetcher();
      apply(d);
      return d;
    } catch (e) {
      if (onError) onError(e);
    } finally {
      setLoading(false);
    }
  }, [fetcher, apply, onError]);

  // نبضة خلفية صامتة: تطبّق فقط عند خمول المستخدم، وإلا تُخزَّن كمعلّقة.
  const tick = useCallback(async () => {
    if (typeof document !== "undefined" && document.hidden) return;
    let d;
    try { d = await fetcher(); } catch { return; }
    const sig = getSignature(d);
    if (sig === sigRef.current) return;      // لا تغيّر → لا إعادة رسم إطلاقًا
    if (isBusy && isBusy()) {
      setPending(d);
      setPendingCount(getNewCount ? getNewCount(d, dataRef.current) : 0);
    } else {
      apply(d);
    }
  }, [fetcher, getSignature, getNewCount, isBusy, apply]);

  useEffect(() => {
    const t = setInterval(tick, intervalMs);
    return () => clearInterval(t);
  }, [tick, intervalMs]);

  const applyPending = useCallback(() => {
    if (dataRef.current === null && pending === null) return;
    setPending((p) => {
      if (p) apply(p);
      return null;
    });
  }, [apply, pending]);

  return {
    data, loading, lastUpdated,
    pending: !!pending, pendingCount,
    refresh, applyPending,
  };
}
